import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ChevronDown, Menu, X } from 'lucide-react';
import AppStoreLinks from '../common/AppStoreLinks';
import { PAGE_TO_PATH } from './seoConfig';

const Header = ({ currentPage = 'home', onNavigate, user }) => {
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const go = (page, e) => {
    if (e) e.preventDefault();
    if (onNavigate) onNavigate(page);
    setIsMobileMenuOpen(false);
    setIsSolutionsOpen(false);
  };

  const navClass = (page) => {
    const active = currentPage === page;
    if (isScrolled) {
      return active
        ? 'text-blue-600 font-semibold'
        : 'text-gray-700 hover:text-blue-600';
    }
    return active
      ? 'text-blue-300 font-semibold'
      : 'text-white hover:text-blue-300';
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200/50'
          : 'bg-gradient-to-r from-blue-900/95 via-blue-800/95 to-indigo-900/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" onClick={() => onNavigate && onNavigate('home')} className="flex items-center group">
              <img
                src="/images/Rink-White-600x309.png"
                alt="RINK Data"
                width={140}
                height={72}
                decoding="async"
                fetchPriority="high"
                className={`h-12 w-auto transition-all duration-300 group-hover:scale-110 ${
                  isScrolled ? 'filter brightness-0' : ''
                }`}
                style={{ maxWidth: 140 }}
              />
            </Link>
          </div>

          <nav className="hidden lg:flex space-x-8" aria-label="Primary">
            <Link
              to="/"
              className={`px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg hover:bg-white/10 ${navClass('home')}`}
              onClick={(e) => go('home', e)}
            >
              HOME
            </Link>
            <Link
              to="/about"
              className={`px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg hover:bg-white/10 ${navClass('about')}`}
              onClick={(e) => go('about', e)}
            >
              ABOUT
            </Link>

            <div className="relative">
              <button
                type="button"
                className={`px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg hover:bg-white/10 flex items-center ${navClass(
                  currentPage === 'web-scraping' || currentPage === 'web-integrations'
                    ? currentPage
                    : 'solutions'
                )}`}
                onMouseEnter={() => setIsSolutionsOpen(true)}
                onMouseLeave={() => setIsSolutionsOpen(false)}
                aria-expanded={isSolutionsOpen}
                aria-haspopup="true"
              >
                SOLUTIONS
                <ChevronDown
                  className={`ml-2 h-4 w-4 transition-transform duration-300 ${
                    isSolutionsOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isSolutionsOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-72 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-gray-200/50 z-50 overflow-hidden"
                  onMouseEnter={() => setIsSolutionsOpen(true)}
                  onMouseLeave={() => setIsSolutionsOpen(false)}
                >
                  <div className="py-2">
                    <Link
                      to="/web-scraping"
                      className="block px-6 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 hover:translate-x-2 border-l-4 border-transparent hover:border-blue-500"
                      onClick={(e) => go('web-scraping', e)}
                    >
                      <div className="font-semibold">Web Scraping Solutions</div>
                      <div className="text-xs text-gray-500 mt-1">All scrapers overview</div>
                    </Link>
                    <Link
                      to="/scrapers/zillow"
                      className="block px-6 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 hover:translate-x-2 border-l-4 border-transparent hover:border-blue-500"
                      onClick={() => {
                        setIsSolutionsOpen(false);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <div className="font-semibold">Zillow Scraper</div>
                    </Link>
                    <Link
                      to="/scrapers/google-business"
                      className="block px-6 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 hover:translate-x-2 border-l-4 border-transparent hover:border-blue-500"
                      onClick={() => {
                        setIsSolutionsOpen(false);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <div className="font-semibold">Google Business Scraper</div>
                    </Link>
                    <Link
                      to="/web-integrations"
                      className="block px-6 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 hover:translate-x-2 border-l-4 border-transparent hover:border-blue-500"
                      onClick={(e) => go('web-integrations', e)}
                    >
                      <div className="font-semibold">Web Integrations</div>
                      <div className="text-xs text-gray-500 mt-1">Connect your systems seamlessly</div>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/pricing"
              className={`px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg hover:bg-white/10 ${navClass('pricing')}`}
              onClick={(e) => go('pricing', e)}
            >
              PRICING
            </Link>
            <Link
              to="/reviews"
              className={`px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg hover:bg-white/10 ${navClass('reviews')}`}
              onClick={(e) => go('reviews', e)}
            >
              REVIEWS
            </Link>
            <Link
              to="/contact"
              className={`px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg hover:bg-white/10 ${navClass('contact')}`}
              onClick={(e) => go('contact', e)}
            >
              CONTACT
            </Link>
          </nav>

          <div className="flex items-center space-x-4">
            <AppStoreLinks
              className="hidden lg:flex"
              appleIconClassName={isScrolled ? 'text-gray-800' : 'text-white'}
            />

            {user ? (
              <Link to="/dashboard">
                <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button
                    variant="outline"
                    className={`${
                      isScrolled
                        ? 'text-blue-600 border-blue-600 hover:bg-blue-50 hover:border-blue-700'
                        : 'text-white border-white hover:bg-white/10 hover:border-white/80'
                    } transition-all duration-300 transform hover:scale-105`}
                  >
                    Login
                  </Button>
                </Link>
                <Button
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  onClick={(e) => go('pricing', e)}
                >
                  Get Started
                </Button>
              </>
            )}

            <button
              type="button"
              className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors duration-300"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className={`h-6 w-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
              ) : (
                <Menu className={`h-6 w-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-200/50">
          <div className="px-4 py-6 space-y-4">
            {[
              ['home', 'HOME'],
              ['about', 'ABOUT'],
              ['web-scraping', 'WEB SCRAPING SOLUTIONS'],
              ['web-integrations', 'WEB INTEGRATIONS'],
              ['pricing', 'PRICING'],
              ['reviews', 'REVIEWS'],
              ['contact', 'CONTACT'],
            ].map(([page, label]) => (
              <Link
                key={page}
                to={PAGE_TO_PATH[page]}
                className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-300 ${
                  currentPage === page
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={(e) => go(page, e)}
              >
                {label}
              </Link>
            ))}

            <div className="pt-4 border-t border-gray-200">
              <p className="px-4 pb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Get the mobile app
              </p>
              <AppStoreLinks className="px-4" appleIconClassName="text-gray-800" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
